import React, { useState, useEffect, useRef, useCallback } from 'react'
import Modal from '@components/common/modal/Modal'
import FlexContainer from '@components/common/FlexContainer'
import { Button, DateInput, Dropdown } from '@components/common/form'
import { AuthorizedInteractable } from '@containers/common/auth'
import { YSpacing } from '@components/common'
import { AutocompleteInput } from '@containers/common/form'
import moment from 'moment-timezone'
import { MultiSelectOption } from '@res/styledComponents/index'
import LoadingIndicator from '@components/common/LoadingIndicator'

interface Chef {
  id: string
  name: string
}

interface Hq {
  id: number
  name: string
}

interface ChefPaySummary {
  ChefId: string
  HqId: number
  PayToName: string
  Count: number
  GrossPay: string
  Taxes: string
  ProcessingFee: string
  VolumeRebate: string
}

interface GenFeeResults {
  chefPaySummaries: ChefPaySummary[]
  dateStr: string
  failedDeletes: string[]
  failedSLSyncOrders: string[]
  failedUpserts: string[]
  missingChefPays: any[]
}

interface FinalizeLateAdjChefPaysResults {
  failedSLSyncOrders: string[]
  syncedOrdersByDate: {
    [key: string]: string[]
  }
}

interface GenFeeResultsAndQuery {
  results: GenFeeResults
  queryText: string
}

// Pay period job types
type JobStatus = 'pending' | 'running' | 'completed' | 'failed'

interface JobProgress {
  currentDateIndex: number
  totalDates: number
  currentDateStr: string
  message: string
}

interface JobResults {
  failedOrderSyncs: Record<string, string[]>
  failedUpserts: Record<string, string[]>
  failedDeletes: Record<string, string[]>
  missingChefPays: Record<string, string[]>
  successfulDates: string[]
  totalFailedOrderSyncs: number
  totalFailedUpserts: number
  totalFailedDeletes: number
  totalMissingChefPays: number
  totalChefPayUpserts: number
}

interface ChefPayJob {
  id: string
  status: JobStatus
  startedAt: string
  completedAt: string | null
  dateStrs: string[]
  triggeredByUserId: string
  triggeredByUserEmail: string
  currentProgress: JobProgress
  results: JobResults
  errors: string[]
  createdAt: string
  updatedAt: string
}

interface TriggerJobResponse {
  jobId: string
  message: string
  error?: boolean
}

// Helper function to calculate pay period dates
const getPayPeriodDates = (): {
  currentPeriodDate: string
  previousPeriodDate: string
} => {
  const now = moment.tz('America/Los_Angeles')
  const today = now.clone().startOf('day')

  // Find the start of the current pay period (most recent Saturday)
  // Saturday is day 6 in moment.js (0 = Sunday)
  const dayOfWeek = today.day()
  const daysSinceSaturday = dayOfWeek === 6 ? 0 : (dayOfWeek + 1) % 7
  const currentPeriodStart = today.clone().subtract(daysSinceSaturday, 'days')

  // Previous period's last day is the Friday before current period start
  const previousPeriodEnd = currentPeriodStart.clone().subtract(1, 'day')

  return {
    currentPeriodDate: today.format('YYYY-MM-DD'),
    previousPeriodDate: previousPeriodEnd.format('YYYY-MM-DD'),
  }
}

interface Props {
  userHeadquarters: Hq[]
  show: boolean
  close: () => void
  generateChefProcessingFees: ({
    chefId,
    processingDate,
    hqIds,
  }: {
    processingDate: string // YYYY-MM-DD
    chefId?: string
    hqIds?: number[]
  }) => Promise<GenFeeResults | undefined>
  finalizeLateAdjChefPaysOnDate: ({
    processingDate,
  }: {
    processingDate: string // YYYY-MM-DD
  }) => Promise<FinalizeLateAdjChefPaysResults | undefined>
  runFinalizeChefPaysForPayPeriod: ({
    processingDate,
  }: {
    processingDate: string // YYYY-MM-DD
  }) => Promise<TriggerJobResponse | undefined>
  getChefPayJobStatus: ({
    jobId,
  }: {
    jobId: string
  }) => Promise<{ job: ChefPayJob } | undefined>
  getRecentChefPayJobs: () => Promise<{ jobs: ChefPayJob[] } | undefined>
  searchChefs: (search: any) => Promise<Chef[] | false>
  showConfirmationModal: (text: string) => Promise<boolean>
}

const POLL_INTERVAL = 3000 // 3 seconds

const CalcChefFeesModal = (props: Props) => {
  const {
    show,
    userHeadquarters,
    close,
    searchChefs,
    showConfirmationModal,
    generateChefProcessingFees,
    runFinalizeChefPaysForPayPeriod,
    getChefPayJobStatus,
  } = props
  const [processingDate, setProcessingDate] = useState<moment.Moment>(moment())
  const [finalizeType, setFinalizeType] = useState<'all' | 'pay-period'>('all')
  const [selectedChef, setSelectedChef] = useState<Chef | null>(null)
  const [selectedHqs, setSelectedHqs] = useState<Hq[]>([])
  const [results, setResults] = useState<GenFeeResultsAndQuery | undefined>(
    undefined,
  )
  const [isProcessing, setIsProcessing] = useState<boolean>(false)

  // Pay period state
  const payPeriodDates = getPayPeriodDates()
  const [selectedPayPeriodDate, setSelectedPayPeriodDate] = useState<string>(
    payPeriodDates.currentPeriodDate,
  )
  const [activeJobId, setActiveJobId] = useState<string | null>(null)
  const [activeJob, setActiveJob] = useState<ChefPayJob | null>(null)
  const [jobError, setJobError] = useState<string | null>(null)
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null)

  // Polling for job status
  const pollJobStatus = useCallback(async () => {
    if (!activeJobId) {
      return
    }

    const response = await getChefPayJobStatus({ jobId: activeJobId })
    if (response?.job) {
      setActiveJob(response.job)

      // Stop polling if job is completed or failed
      if (
        response.job.status === 'completed' ||
        response.job.status === 'failed'
      ) {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current)
          pollIntervalRef.current = null
        }
        setIsProcessing(false)
      }
    }
  }, [activeJobId, getChefPayJobStatus])

  // Start polling when activeJobId changes
  useEffect(() => {
    if (activeJobId && isProcessing) {
      // Initial poll
      pollJobStatus()

      // Set up polling interval
      pollIntervalRef.current = setInterval(pollJobStatus, POLL_INTERVAL)
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
        pollIntervalRef.current = null
      }
    }
  }, [activeJobId, isProcessing, pollJobStatus])

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
      }
    }
  }, [])

  const onChangeProcDate = (value: moment.Moment) => {
    setProcessingDate(value)
  }

  const onChangeFinalizeType = (type: 'all' | 'pay-period') => {
    setFinalizeType(type)
    setResults(undefined)
    setActiveJob(null)
    setActiveJobId(null)
    setJobError(null)
  }

  const onExecutePayPeriodFinalize = async (): Promise<void> => {
    const confirmText = `Are you sure you want to finalize chef payouts for the pay period containing ${selectedPayPeriodDate}? This may take several minutes.`
    const doFinalize = await showConfirmationModal(confirmText)

    if (doFinalize) {
      // Clear previous job state BEFORE setting isProcessing to avoid
      // triggering polling with the old job ID
      setActiveJobId(null)
      setActiveJob(null)
      setJobError(null)
      setIsProcessing(true)

      const response = await runFinalizeChefPaysForPayPeriod({
        processingDate: selectedPayPeriodDate,
      })

      if (response) {
        if (response.error) {
          // Handle 409 conflict - job already running
          setJobError(response.message || 'A job is already running')
          if (response.jobId) {
            setActiveJobId(response.jobId)
          }
          setIsProcessing(false)
        } else if (response.jobId) {
          setActiveJobId(response.jobId)
          // Polling will start automatically via useEffect
        }
      } else {
        setIsProcessing(false)
      }
    }
  }

  const onExecuteFinalize = async (): Promise<void> => {
    const processingDateStr = processingDate.format('YYYY-MM-DD')

    if (finalizeType === 'all') {
      setResults(undefined)
      const chefStr = selectedChef ? `Chef ${selectedChef.name}` : 'all chefs'
      const hqsStr = selectedHqs.length
        ? `markets ${selectedHqs.map((hq) => hq.name).join(', ')}`
        : `all markets`
      const queryText = `date ${processingDateStr} for ${chefStr} in ${hqsStr}`
      const confirmText = `Are you sure you want to finalize all payouts for ${queryText}?`
      const doGenerate = await showConfirmationModal(confirmText)
      if (doGenerate) {
        setIsProcessing(true)
        const genFeeResults = await generateChefProcessingFees({
          processingDate: processingDateStr,
          chefId: selectedChef?.id,
          hqIds: selectedHqs.length
            ? selectedHqs.map((hq) => hq.id)
            : undefined,
        })
        setIsProcessing(false)
        if (genFeeResults) {
          setResults({
            results: genFeeResults,
            queryText,
          })
        }
      }
    } else if (finalizeType === 'pay-period') {
      await onExecutePayPeriodFinalize()
    }
  }

  const getFinalizeTypeDescription = () => {
    switch (finalizeType) {
      case 'all':
        return 'Re-generates chef processing fees for a single selected date, chef(s), and market(s).'
      case 'pay-period':
        return 'Runs chef pay finalization for an entire pay period (Saturday to Friday). This job may take several minutes to complete.'
      default:
        return ''
    }
  }

  const getFinalizeButtonLabel = () => {
    switch (finalizeType) {
      case 'all':
        return 'Finalize All Payouts'
      case 'pay-period':
        return 'Run Pay Period Finalization'
      default:
        return 'Finalize'
    }
  }

  const renderJobProgress = () => {
    if (!activeJob) {
      return null
    }

    const { status, currentProgress, dateStrs, triggeredByUserEmail } =
      activeJob
    const isRunning = status === 'pending' || status === 'running'

    return (
      <div className="flex flex-col border border-solid rounded border-blue-400 p-4 mt-4 w-full">
        <div className="flex flex-row justify-between items-center mb-3">
          <div className="bold text-lg">
            Job Status:{' '}
            <span
              className={`${
                status === 'completed'
                  ? 'text-green-600'
                  : status === 'failed'
                    ? 'text-red-600'
                    : 'text-blue-600'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
          </div>
          {isRunning && <LoadingIndicator />}
        </div>

        <div className="text-sm mb-2">
          <span className="font-semibold">Job ID:</span> {activeJob.id}
        </div>
        <div className="text-sm mb-2">
          <span className="font-semibold">Triggered by:</span>{' '}
          {triggeredByUserEmail}
        </div>
        <div className="text-sm mb-2">
          <span className="font-semibold">Dates:</span>{' '}
          {dateStrs?.join(', ') || 'N/A'}
        </div>

        {currentProgress && isRunning && (
          <div className="mt-3 p-3 bg-gray-100 rounded">
            <div className="font-semibold mb-2">Progress:</div>
            <div className="text-sm">
              Processing date {currentProgress.currentDateIndex + 1} of{' '}
              {currentProgress.totalDates}: {currentProgress.currentDateStr}
            </div>
            <div className="text-sm text-gray-600 mt-1">
              {currentProgress.message}
            </div>
            <div className="w-full bg-gray-300 rounded-full h-2 mt-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{
                  width: `${
                    ((currentProgress.currentDateIndex + 1) /
                      currentProgress.totalDates) *
                    100
                  }%`,
                }}
              />
            </div>
          </div>
        )}
      </div>
    )
  }

  const renderJobResults = () => {
    if (
      !activeJob ||
      (activeJob.status !== 'completed' && activeJob.status !== 'failed')
    ) {
      return null
    }

    const { results, errors, status } = activeJob

    return (
      <div
        className="flex flex-col mt-4 w-full overflow-y-auto"
        style={{ maxHeight: '300px' }}
      >
        {status === 'failed' && errors && errors.length > 0 && (
          <div className="flex flex-col border border-solid rounded border-red-400 p-4 mb-4">
            <div className="bold text-center text-red-600 mb-2">Errors</div>
            <ul className="ml-4 text-sm">
              {errors.map((error, idx) => (
                <li key={idx} className="text-red-600">
                  {error}
                </li>
              ))}
            </ul>
          </div>
        )}

        {results && (
          <>
            {/* Summary Stats */}
            <div className="flex flex-row gap-4 mb-4">
              <div className="flex flex-col border border-solid rounded border-green-400 p-3 flex-1 text-center">
                <div className="text-2xl font-bold text-green-600">
                  {results.successfulDates?.length || 0}
                </div>
                <div className="text-sm">Successful Dates</div>
              </div>
              <div className="flex flex-col border border-solid rounded border-blue-400 p-3 flex-1 text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {results.totalFailedOrderSyncs || 0}
                </div>
                <div className="text-sm">Failed Order Syncs</div>
              </div>
              <div className="flex flex-col border border-solid rounded border-blue-400 p-3 flex-1 text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {results.totalFailedUpserts || 0}
                </div>
                <div className="text-sm">
                  Failed Chef Payout Inserts / Updates
                </div>
              </div>
              <div className="flex flex-col border border-solid rounded border-blue-400 p-3 flex-1 text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {results.totalFailedDeletes || 0}
                </div>
                <div className="text-sm">Failed Chef Payout Deletes</div>
              </div>
              <div className="flex flex-col border border-solid rounded border-blue-400 p-3 flex-1 text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {results.totalChefPayUpserts || 0}
                </div>
                <div className="text-sm">Chef Payout Inserts / Updates</div>
              </div>
            </div>

            {/* Failed Items */}
            <div className="flex flex-row gap-4 flex-wrap">
              {results.failedOrderSyncs &&
                Object.keys(results.failedOrderSyncs).length > 0 && (
                  <div className="flex flex-col border border-solid rounded border-red-400 p-3 flex-1 min-w-[200px]">
                    <div className="bold text-center text-red-600 mb-2">
                      Failed Order Syncs
                    </div>
                    {Object.entries(results.failedOrderSyncs).map(
                      ([date, orderIds]) => (
                        <div key={date} className="mb-2">
                          <div className="font-semibold text-sm">
                            {date}: ({orderIds.length || 0})
                          </div>
                        </div>
                      ),
                    )}
                  </div>
                )}

              {results.failedUpserts &&
                Object.keys(results.failedUpserts).length > 0 && (
                  <div className="flex flex-col border border-solid rounded border-red-400 p-3 flex-1 min-w-[200px]">
                    <div className="bold text-center text-red-600 mb-2">
                      Failed Upserts
                    </div>
                    {Object.entries(results.failedUpserts).map(
                      ([date, errors]) => (
                        <div key={date} className="mb-2">
                          <div className="font-semibold text-sm">{date}:</div>
                          <ul className="ml-4 text-xs">
                            {errors.map((error, idx) => (
                              <li key={idx}>{error}</li>
                            ))}
                          </ul>
                        </div>
                      ),
                    )}
                  </div>
                )}

              {results.failedDeletes &&
                Object.keys(results.failedDeletes).length > 0 && (
                  <div className="flex flex-col border border-solid rounded border-red-400 p-3 flex-1 min-w-[200px]">
                    <div className="bold text-center text-red-600 mb-2">
                      Failed Deletes
                    </div>
                    {Object.entries(results.failedDeletes).map(
                      ([date, errors]) => (
                        <div key={date} className="mb-2">
                          <div className="font-semibold text-sm">{date}:</div>
                          <ul className="ml-4 text-xs">
                            {errors.map((error, idx) => (
                              <li key={idx}>{error}</li>
                            ))}
                          </ul>
                        </div>
                      ),
                    )}
                  </div>
                )}

              {/* TODO: Add missing chef pays handling */}

              {/* {results.missingChefPays && Object.keys(results.missingChefPays).length > 0 && (
                <div className="flex flex-col border border-solid rounded border-yellow-400 p-3 flex-1 min-w-[200px]">
                  <div className="bold text-center text-yellow-600 mb-2">
                    Missing Chef Pays ({results.totalMissingChefPays || 0})
                  </div>
                  {Object.entries(results.missingChefPays).map(([date, payIds]) => (
                    <div key={date} className="mb-2">
                      <div className="font-semibold text-sm">{date}:</div>
                      <ul className="ml-4 text-xs">
                        {payIds.map((id, idx) => (
                          <li key={idx}>{id}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )} */}
            </div>

            {/* Successful Dates */}
            {results.successfulDates && results.successfulDates.length > 0 && (
              <div className="flex flex-col border border-solid rounded border-green-400 p-3 mt-4">
                <div className="bold text-center text-green-600 mb-2">
                  Successful Dates
                </div>
                <div className="text-sm text-center">
                  {results.successfulDates.join(', ')}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    )
  }

  return (
    <Modal
      show={show}
      title="Finalize Chef Payouts"
      hideModal={close}
      color="#001940"
      width="800px"
      height="700px"
    >
      <FlexContainer
        justifyContent="space-between"
        flexWrap="wrap"
        flexDirection="row"
      >
        <p className="bold mb-5">{getFinalizeTypeDescription()}</p>

        <div className="flex flex-row w-full justify-between flex-wrap gap-4">
          <div className="flex flex-col">
            <Dropdown
              label="Finalize Type"
              width="100%"
              marginBottom="0"
              value={finalizeType}
              onChange={(e) =>
                onChangeFinalizeType(e.target.value as 'all' | 'pay-period')
              }
            >
              <option value="all">Single Date - All Payouts</option>
              <option value="pay-period">Full Pay Period</option>
            </Dropdown>
          </div>

          {finalizeType !== 'pay-period' && (
            <div className="flex flex-col">
              <DateInput
                dateFormat="Y-m-d"
                width="100%"
                label="Processing Date"
                date={processingDate}
                onChange={onChangeProcDate}
                // date of deployment of this feature since prior fee periods are calculated per week
                minDate={moment('2024-07-28')}
              />
            </div>
          )}

          {finalizeType === 'pay-period' && (
            <div className="flex flex-col">
              <Dropdown
                label="Pay Period End Date"
                width="100%"
                marginBottom="0"
                value={selectedPayPeriodDate}
                onChange={(e) => setSelectedPayPeriodDate(e.target.value)}
              >
                <option value={payPeriodDates.currentPeriodDate}>
                  Current Period - {payPeriodDates.currentPeriodDate} (Today)
                </option>
                <option value={payPeriodDates.previousPeriodDate}>
                  Previous Period - {payPeriodDates.previousPeriodDate} (Last
                  Friday)
                </option>
              </Dropdown>
            </div>
          )}

          {finalizeType === 'all' && (
            <>
              <div className="flex flex-col">
                <AutocompleteInput
                  label="Chef"
                  loaderFunction={(search) => searchChefs({ ...search })}
                  onSelect={(value: Chef | false) =>
                    setSelectedChef(value || null)
                  }
                />
                <YSpacing height="5px" />
                <FlexContainer flexWrap="wrap">
                  {selectedChef && (
                    <MultiSelectOption onClick={() => setSelectedChef(null)}>
                      {selectedChef.name}
                    </MultiSelectOption>
                  )}
                  {!selectedChef && <MultiSelectOption>All</MultiSelectOption>}
                </FlexContainer>
              </div>

              <div className="flex flex-col">
                <Dropdown
                  label="Market(s)"
                  width="100%"
                  marginBottom="0"
                  onChange={(e) => {
                    const selectedValue = e.target.value
                    if (!selectedValue) {
                      return
                    }
                    const selectedHq = userHeadquarters.find(
                      (userHq) => selectedValue === `${userHq.id}`,
                    )
                    if (selectedHq) {
                      setSelectedHqs([...selectedHqs, selectedHq])
                    }
                  }}
                >
                  <option value=""> - </option>
                  {userHeadquarters
                    .filter(
                      (opt) => !selectedHqs.find((hq) => hq.id === opt.id),
                    )
                    .map((opt) => (
                      <option key={opt.id} value={`${opt.id}`}>
                        {opt.name}
                      </option>
                    ))}
                </Dropdown>
                <YSpacing height="5px" />
                <FlexContainer flexWrap="wrap">
                  {selectedHqs.map((hq) => (
                    <MultiSelectOption
                      key={hq.id}
                      onClick={() =>
                        setSelectedHqs(
                          selectedHqs.filter((hqOpt) => hqOpt.id !== hq.id),
                        )
                      }
                    >
                      {hq.name}
                    </MultiSelectOption>
                  ))}
                  {selectedHqs.length === 0 && (
                    <MultiSelectOption>All</MultiSelectOption>
                  )}
                </FlexContainer>
              </div>
            </>
          )}
        </div>

        {jobError && (
          <div className="mt-4 p-3 bg-red-100 border border-red-400 rounded text-red-700 w-full">
            {jobError}
          </div>
        )}

        <div className="mt-6 flex flex-align-center items-center justify-center w-full">
          {isProcessing && finalizeType !== 'pay-period' ? (
            <LoadingIndicator />
          ) : (
            <AuthorizedInteractable roles={['master admin', 'finance']}>
              <Button
                onClick={onExecuteFinalize}
                label={getFinalizeButtonLabel()}
                disabled={isProcessing}
              />
            </AuthorizedInteractable>
          )}
        </div>

        {/* Pay Period Job Status */}
        {finalizeType === 'pay-period' && activeJob && renderJobProgress()}
        {finalizeType === 'pay-period' && activeJob && renderJobResults()}

        {results && (
          <div className="flex flex-col overflow-y-scroll h-100 justify-center mt-10 w-full">
            <div className="flex flex-col justify-center w-full">
              <div className="bold text-center underline mb-5">
                Processing fees generated for {results.queryText}
              </div>

              {/* Chef Pay Summaries */}
              <div className="flex flex-col border border-solid rounded border-gray-400 p-4 mb-4">
                <div className="bold text-center mb-3">
                  Generated fees for {results.results.chefPaySummaries.length}{' '}
                  chef(s)
                </div>
                {results.results.chefPaySummaries.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-2">Chef ID</th>
                          <th className="text-left p-2">Pay To Name</th>
                          <th className="text-right p-2">HQ</th>
                          <th className="text-right p-2">Count</th>
                          <th className="text-right p-2">Gross Pay</th>
                          <th className="text-right p-2">Processing Fee</th>
                          <th className="text-right p-2">Taxes</th>
                          <th className="text-right p-2">Volume Rebate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {results.results.chefPaySummaries.map(
                          (summary, idx) => (
                            <tr key={idx} className="border-b">
                              <td className="p-2 font-mono text-xs">
                                {summary.ChefId.substring(0, 8)}...
                              </td>
                              <td className="p-2">
                                {summary.PayToName || '-'}
                              </td>
                              <td className="text-right p-2">{summary.HqId}</td>
                              <td className="text-right p-2">
                                {summary.Count}
                              </td>
                              <td className="text-right p-2">
                                ${summary.GrossPay}
                              </td>
                              <td className="text-right p-2">
                                ${summary.ProcessingFee}
                              </td>
                              <td className="text-right p-2">
                                ${summary.Taxes}
                              </td>
                              <td className="text-right p-2">
                                ${summary.VolumeRebate}
                              </td>
                            </tr>
                          ),
                        )}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-center">No chef pay summaries</p>
                )}
              </div>

              {/* Errors and Issues */}
              <div className="flex flex-row gap-4">
                {/* Failed Deletes */}
                {results.results.failedDeletes.length > 0 && (
                  <div className="flex flex-col border border-solid rounded border-red-400 p-4 flex-1">
                    <div className="bold text-center text-red-600 mb-2">
                      Failed Deletes ({results.results.failedDeletes.length})
                    </div>
                    <ul className="ml-4 text-sm">
                      {results.results.failedDeletes.map((id, idx) => (
                        <li key={idx}>{id}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Failed SL Sync Orders */}
                {results.results.failedSLSyncOrders.length > 0 && (
                  <div className="flex flex-col border border-solid rounded border-red-400 p-4 flex-1">
                    <div className="bold text-center text-red-600 mb-2">
                      Failed SL Sync Orders (
                      {results.results.failedSLSyncOrders.length})
                    </div>
                    <ul className="ml-4 text-sm">
                      {results.results.failedSLSyncOrders.map((id, idx) => (
                        <li key={idx}>{id}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Failed Upserts */}
                {results.results.failedUpserts.length > 0 && (
                  <div className="flex flex-col border border-solid rounded border-red-400 p-4 flex-1">
                    <div className="bold text-center text-red-600 mb-2">
                      Failed Upserts ({results.results.failedUpserts.length})
                    </div>
                    <ul className="ml-4 text-sm">
                      {results.results.failedUpserts.map((id, idx) => (
                        <li key={idx}>{id}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Missing Chef Pays */}
                {results.results.missingChefPays.length > 0 && (
                  <div className="flex flex-col border border-solid rounded border-yellow-400 p-4 flex-1">
                    <div className="bold text-center text-yellow-600 mb-2">
                      Missing Chef Pays (
                      {results.results.missingChefPays.length})
                    </div>
                    <p className="text-center text-sm">
                      {results.results.missingChefPays.length} chef pay(s)
                      missing
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </FlexContainer>
    </Modal>
  )
}

export default CalcChefFeesModal
